import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

const MultiSelectDropdown = ({
  options = [],
  selectedValues = [],
  onChange,
  disabled = false,
  placeholder = "Select options"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleSelectOption = (optionName) => {
    if (selectedValues.includes(optionName)) {
      onChange(selectedValues.filter(v => v !== optionName));
    } else {
      onChange([...selectedValues, optionName]);
    }
  };

  const handleSelectAll = () => {
    if (selectedValues.length === options.length) {
      onChange([]);
    } else {
      onChange(options.map(opt => opt.name));
    }
  };

  const isAllSelected = options.length > 0 && selectedValues.length === options.length;

  return (
    <div ref={dropdownRef} style={{ position: 'relative', width: '100%' }}>
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        style={{
          width: '100%',
          padding: '11px 14px',
          fontSize: '14px',
          border: '1px solid #d1d5db',
          borderRadius: '8px',
          backgroundColor: disabled ? '#f3f4f6' : 'white',
          color: disabled ? '#9ca3af' : '#374151',
          cursor: disabled ? 'not-allowed' : 'pointer',
          textAlign: 'left',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          transition: 'all 0.2s',
          minHeight: '44px',
        }}
      >
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '6px',
          flex: 1,
          alignItems: 'center',
        }}>
          {selectedValues.length === 0 ? (
            <span style={{ color: '#9ca3af' }}>{placeholder}</span>
          ) : (
            <>
              {selectedValues.slice(0, 4).map((value) => (
                <span
                  key={value}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '2px 8px',
                    fontSize: '12px',
                    fontWeight: '500',
                    borderRadius: '9999px',
                    backgroundColor: '#dbeafe',
                    color: '#1e40af',
                  }}
                >
                  {value}
                </span>
              ))}
              {selectedValues.length > 4 && (
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '2px 8px',
                  fontSize: '12px',
                  fontWeight: '500',
                  borderRadius: '9999px',
                  backgroundColor: '#f3f4f6',
                  color: '#4b5563',
                }}>
                  +{selectedValues.length - 4} more
                </span>
              )}
            </>
          )}
        </div>
        <svg
          style={{
            width: '16px',
            height: '16px',
            marginLeft: '8px',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
          }}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && !disabled && (
        <div
          style={{
            position: 'fixed',
            top: dropdownRef.current?.getBoundingClientRect().bottom + 4 || 0,
            left: dropdownRef.current?.getBoundingClientRect().left || 0,
            width: dropdownRef.current?.getBoundingClientRect().width || 'auto',
            backgroundColor: 'white',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            zIndex: 10002,
            maxHeight: '250px',
            overflowY: 'auto',
          }}
        >
          {options.length > 0 && (
            <button
              onClick={handleSelectAll}
              type="button"
              style={{
                padding: '10px 14px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                borderBottom: '1px solid #e5e7eb',
                backgroundColor: '#f9fafb',
                fontWeight: '600',
                fontSize: '13px',
                color: '#374151',
                border: 'none',
                width: '100%',
                textAlign: 'left',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
            >
              <input
                type="checkbox"
                checked={isAllSelected}
                onChange={() => { }}
                style={{
                  marginRight: '10px',
                  cursor: 'pointer',
                  width: '16px',
                  height: '16px',
                }}
              />
              <span>Select All</span>
            </button>
          )}

          {options.length === 0 ? (
            <div style={{ padding: '16px', textAlign: 'center', color: '#9ca3af', fontSize: '14px' }}>
              No options available
            </div>
          ) : (
            options.map((option) => (
              <button
                key={option.object_id}
                onClick={() => handleSelectOption(option.name)}
                type="button"
                style={{
                  padding: '10px 14px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '14px',
                  color: '#374151',
                  transition: 'background-color 0.15s',
                  border: 'none',
                  backgroundColor: 'white',
                  width: '100%',
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
              >
                <input
                  type="checkbox"
                  checked={selectedValues.includes(option.name)}
                  onChange={() => { }}
                  style={{
                    marginRight: '10px',
                    cursor: 'pointer',
                    width: '16px',
                    height: '16px',
                  }}
                />
                <div style={{ fontWeight: '500' }}>
                  {option.description || option.name}
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

MultiSelectDropdown.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      object_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      description: PropTypes.string,
    })
  ),
  selectedValues: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
};

export default MultiSelectDropdown;